import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { getDashboardMetricInsights } from "../api/client";
import {
  DashboardKpiGridSkeleton,
  DashboardKpiSkeleton,
  DashboardTabChrome,
} from "./dashboard/DashboardTabChrome";
import DashboardRankedBarChart from "./dashboard/DashboardRankedBarChart";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { useDashboardWithPresentation } from "../hooks/useDashboardWithPresentation";
import { useDashboardTabState } from "../hooks/useDashboardTabState";

const BR = (n) => Number(n || 0).toLocaleString("pt-BR");
const BRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const secToHuman = (sec) => {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
};

const SOURCE_LABELS = {
  campaign: "Envio ativo",
  whatsapp_active: "WhatsApp (legado)",
  ctwa: "Anúncios",
  other: "Outras",
};

function KpiCard({ icon, title, value, color, sub, titleAction }) {
  return (
    <Card sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {title}
              </Typography>
              {titleAction}
            </Stack>
            <Typography variant="h4" sx={{ color, lineHeight: 1.1, fontWeight: 600 }}>
              {value}
            </Typography>
            {sub ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                {sub}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmptyHint({ children }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, py: 0.5 }}>
      {children}
    </Typography>
  );
}

function Panel({ title, subtitle, action, children }) {
  return (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <CardContent sx={{ p: { xs: 2.25, sm: 2.75 }, "&:last-child": { pb: { xs: 2.25, sm: 2.75 } } }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: subtitle ? 0.5 : 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {action}
        </Stack>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75, lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

/** Linha quantitativa principal; receita/custo só como detalhe secundário quando existem. */
function CampaignRow({
  title,
  primary,
  secondary,
  revenue,
  cost,
  action,
  onClick,
  expandIcon,
}) {
  const revenueN = Number(revenue || 0);
  const costN = Number(cost || 0);
  const showMoney = revenueN > 0 || costN > 0;
  const Wrapper = onClick ? "button" : "div";
  return (
    <Box
      sx={{
        py: 1.35,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: 0, pb: 0 },
      }}
    >
      <Box
        component={Wrapper}
        type={onClick ? "button" : undefined}
        onClick={onClick}
        sx={
          onClick
            ? {
                all: "unset",
                cursor: "pointer",
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                "&:hover .row-title": { color: "primary.main" },
              }
            : undefined
        }
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography className="row-title" variant="body2" fontWeight={600} noWrap>
              {title}
            </Typography>
            {primary ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
                {primary}
              </Typography>
            ) : null}
            {secondary ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}>
                {secondary}
              </Typography>
            ) : null}
            {showMoney ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.55, lineHeight: 1.4 }}>
                {revenueN > 0 ? `Receita ${BRL(revenueN)}` : null}
                {revenueN > 0 && costN > 0 ? " · " : null}
                {costN > 0 ? `Custo ${BRL(costN)}` : null}
              </Typography>
            ) : null}
            {action ? <Box sx={{ mt: 0.75 }}>{action}</Box> : null}
          </Box>
          {expandIcon || null}
        </Stack>
      </Box>
    </Box>
  );
}

function MoneyCell({ value, tone }) {
  const n = Number(value) || 0;
  if (!(n > 0)) {
    return (
      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em" }}
      >
        —
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      fontWeight={600}
      color={tone === "success" ? "success.main" : "text.primary"}
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      {BRL(n)}
    </Typography>
  );
}

function FinanceColHead() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.25,
        py: 0.75,
        mb: 0.25,
        borderRadius: 1,
        bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.06 : 0.03),
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flex: 1, fontWeight: 600, letterSpacing: "0.02em" }}
      >
        Origem
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 52, textAlign: "right", fontWeight: 600 }}
      >
        Vendas
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 92, textAlign: "right", fontWeight: 600 }}
      >
        Receita
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 92, textAlign: "right", fontWeight: 600 }}
      >
        Custo
      </Typography>
    </Stack>
  );
}

function FinanceRow({ title, sales, revenue, cost }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.25,
        py: 1.05,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
        transition: "background-color 0.15s ease",
        "&:hover": {
          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.08 : 0.04),
        },
      }}
    >
      <Typography variant="body2" noWrap title={title} sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: 52, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
      >
        {BR(sales)}
      </Typography>
      <Box sx={{ width: 92, display: "flex", justifyContent: "flex-end" }}>
        <MoneyCell value={revenue} tone="success" />
      </Box>
      <Box sx={{ width: 92, display: "flex", justifyContent: "flex-end" }}>
        <MoneyCell value={cost} />
      </Box>
    </Stack>
  );
}

function FinanceSummaryChip({ label, value, muted }) {
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.15,
        borderRadius: 2,
        minWidth: { xs: "100%", sm: 132 },
        bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.08 : 0.035),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontSize: "0.65rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color={muted ? "text.disabled" : "text.primary"}
        sx={{ mt: 0.25, fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function CampaignOriginDashboard({
  start,
  end,
  ids_origens = [],
  ids_campaigns = [],
  ids_whatsapp_campaigns = [],
  ids_status = [],
  readOnly = false,
  presentationSnapshot = null,
}) {
  const { t } = useTranslation("settings");
  const { t: tChat } = useTranslation("chat");
  const theme = useTheme();
  const navigate = useNavigate();
  const series = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary?.main ?? theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ],
    [theme]
  );

  const [groupBy, setGroupBy] = useState("uf");
  const [expandedCtwaKey, setExpandedCtwaKey] = useState(null);

  const summaryParams = useMemo(
    () => ({
      start,
      end,
      group_by: groupBy,
      ...(Array.isArray(ids_status) && ids_status.length ? { ids_status } : {}),
      ...(Array.isArray(ids_origens) && ids_origens.length ? { ids_origens } : {}),
      ...(Array.isArray(ids_campaigns) && ids_campaigns.length ? { ids_campaigns } : {}),
      ...(Array.isArray(ids_whatsapp_campaigns) && ids_whatsapp_campaigns.length
        ? { ids_whatsapp_campaigns }
        : {}),
    }),
    [start, end, groupBy, ids_status, ids_origens, ids_campaigns, ids_whatsapp_campaigns]
  );

  const summaryQ = useDashboardWithPresentation(
    "origem",
    "summary",
    summaryParams,
    { staleTime: 900_000, enabled: true },
    presentationSnapshot
  );
  const tab = useDashboardTabState(summaryQ, {
    registerShare: !readOnly && !presentationSnapshot,
  });
  const loading = tab.showSkeletons;
  const error = tab.errorMessage || "";
  const data = tab.data?.origin || null;
  const ctwa = tab.data?.ctwa || null;
  const region = tab.data?.region || null;
  const sales = tab.data?.sales || null;

  const totals = data?.totals || {};

  const channelChart = useMemo(
    () =>
      (data?.byChannel || []).map((row) => ({
        name: row.originName || `Canal #${row.originId}`,
        conversations: Number(row.conversations || 0),
      })),
    [data]
  );

  const regionChart = useMemo(
    () =>
      (region?.rows || []).map((row) => ({
        name: row.label || String(row.key || "—"),
        conversations: Number(row.conversations || 0),
      })),
    [region]
  );

  const salesBySource = useMemo(
    () =>
      (sales?.bySource || []).map((row) => ({
        name: SOURCE_LABELS[row.sourceKind] || row.sourceKind,
        revenue: Number(row.revenue || 0),
        cost: Number(row.cost || 0),
        sales: Number(row.sales || 0),
      })),
    [sales]
  );

  const campaignSales = useMemo(() => {
    const generic = sales?.byCampaign || [];
    const legacy = sales?.byWhatsappCampaign || [];
    return (generic.length ? generic : legacy).map((r) => ({
      ...r,
      revenue: Number(r.revenue || 0),
      cost: Number(r.cost || 0),
      sales: Number(r.sales || 0),
    }));
  }, [sales]);

  const ctwaSales = useMemo(
    () =>
      (sales?.byCtwa || []).map((r) => ({
        ...r,
        revenue: Number(r.revenue || 0),
        cost: Number(r.cost || 0),
        sales: Number(r.sales || 0),
      })),
    [sales]
  );

  const salesSummary = useMemo(() => {
    if (salesBySource.length) {
      return {
        sales: salesBySource.reduce((s, r) => s + r.sales, 0),
        revenue: salesBySource.reduce((s, r) => s + r.revenue, 0),
        cost: salesBySource.reduce((s, r) => s + r.cost, 0),
      };
    }
    const rows = [...campaignSales, ...ctwaSales];
    return {
      sales: rows.reduce((s, r) => s + r.sales, 0),
      revenue: rows.reduce((s, r) => s + r.revenue, 0),
      cost: rows.reduce((s, r) => s + r.cost, 0),
    };
  }, [salesBySource, campaignSales, ctwaSales]);

  const hasAnyFinance =
    salesSummary.sales > 0 || salesSummary.revenue > 0 || salesSummary.cost > 0;
  const hasMoneyFilled = salesSummary.revenue > 0 || salesSummary.cost > 0;

  const ctwaConversationCount = useMemo(() => {
    const fromOriginTotals = Number(totals.ctwa || 0);
    if (fromOriginTotals > 0) return fromOriginTotals;
    const fromCtwaTotals = Number(ctwa?.totals?.conversations || ctwa?.total || 0);
    if (fromCtwaTotals > 0) return fromCtwaTotals;
    return (ctwa?.campaigns || []).reduce(
      (sum, row) => sum + Number(row.conversations || row.count || 0),
      0
    );
  }, [totals.ctwa, ctwa]);

  const salesEmptyHint = () => {
    if (ctwaConversationCount > 0) {
      return `Há ${BR(ctwaConversationCount)} conversas de anúncio, mas ainda sem venda concluída neste período.`;
    }
    return "Sem vendas atribuídas a campanhas neste período.";
  };

  const activeCampaigns = useMemo(() => {
    const fromOrigin = data?.byActiveCampaign || [];
    if (fromOrigin.length) return fromOrigin;
    return data?.activeCampaignMetrics || [];
  }, [data]);

  const empty =
    tab.showContent &&
    !tab.isDeferred &&
    !error &&
    !Number(totals.conversations || 0) &&
    !(ctwa?.campaigns || []).length &&
    !(region?.rows || []).length &&
    !(sales?.bySource || []).length;

  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = useState(null);
  const [metricDialogName, setMetricDialogName] = useState("");
  const [metricDialogLabel, setMetricDialogLabel] = useState("");
  const [metricDialogData, setMetricDialogData] = useState(null);
  const [metricDialogError, setMetricDialogError] = useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = useState(null);
  const lastCfgRef = useRef(null);

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricLabel: cfg.metricLabel || cfg.metricName,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: { start, end },
        summaryContext: {
          conversations: totals.conversations,
          ctwa: totals.ctwa,
          activeCampaign: totals.activeCampaign,
          other: totals.other,
          ctwaConversationCount,
          salesSummary,
        },
        filters: {
          ids_status,
          ids_origens,
          ids_campaigns,
          ids_whatsapp_campaigns,
          groupBy,
        },
      };
      return getDashboardMetricInsights("origem", payload);
    },
    {
      onSuccess: (res) => {
        setMetricDialogData(res || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    lastCfgRef.current = cfg;
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => {
    if (readOnly) return null;
    return (
      <Tooltip title={tChat("analytics.explainAi")}>
        <IconButton size="small" onClick={() => openAi(cfg)} aria-label={tChat("analytics.explainAiAria")}>
          <AutoAwesomeIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box>
      <DashboardTabChrome
        isDeferred={tab.isDeferred}
        isPreview={tab.isPreview}
        isInitialLoading={tab.showSkeletons}
        errorMessage={tab.errorMessage}
        previewPendingMessage={t("dashboardPage.shared.previewSectionPending", {
          defaultValue: "Carregando o relatório completo…",
        })}
        emptyState={
          empty
            ? {
                visible: true,
                title: "Nenhuma conversa de campanha neste período",
                hint: "Quando houver disparos, anúncios ou vendas, o panorama aparece aqui.",
              }
            : null
        }
      />

      {/* ——— Volume: o núcleo da análise de campanha ——— */}
      {loading && !data ? (
        <DashboardKpiGridSkeleton count={4} spacing={2} sx={{ mb: 2.5 }} />
      ) : (
        <Grid container spacing={2} sx={{ mb: 2.5 }} alignItems="stretch">
          {[
            {
              key: "all",
              icon: <ForumOutlinedIcon />,
              title: "Conversas",
              value: BR(totals.conversations),
              color: series[0],
              sub: "Total no período",
              metric: "campaign_conversations",
              def: "Total de conversas no período com os filtros da aba Campanhas.",
            },
            {
              key: "ads",
              icon: <AdsClickOutlinedIcon />,
              title: "De anúncios",
              value: BR(totals.ctwa),
              color: series[1],
              sub: "Meta ou Google",
              metric: "campaign_ctwa",
              def: "Conversas originadas de anúncios (Meta CTWA ou Google com mensagem inicial).",
            },
            {
              key: "send",
              icon: <CampaignOutlinedIcon />,
              title: "De disparos",
              value: BR(totals.activeCampaign),
              color: series[2],
              sub: "Envio ativo",
              metric: "campaign_active",
              def: "Conversas ligadas a disparos (WhatsApp, e-mail ou SMS).",
            },
            {
              key: "other",
              icon: <HubOutlinedIcon />,
              title: "Outras",
              value: BR(totals.other),
              color: series[3],
              sub: "Sem campanha",
              metric: "campaign_other",
              def: "Conversas sem vínculo de anúncio nem de disparo.",
            },
          ].map((kpi) => (
            <Grid item xs={12} sm={6} md={3} key={kpi.key} sx={{ display: "flex" }}>
              {loading ? (
                <DashboardKpiSkeleton />
              ) : (
                <KpiCard
                  icon={kpi.icon}
                  title={kpi.title}
                  value={kpi.value}
                  color={kpi.color}
                  sub={kpi.sub}
                  titleAction={
                    <AiBtn
                      cfg={{
                        metric: kpi.metric,
                        metricName: kpi.title,
                        metricDefinition: kpi.def,
                        metricValue: kpi.value,
                        metricHuman: String(kpi.value),
                      }}
                    />
                  }
                />
              )}
            </Grid>
          ))}
        </Grid>
      )}

      {/* ——— Origens quantitativas ——— */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Panel
            title="Anúncios"
            subtitle="Volume por origem. Clique para ver conversas."
            action={
              <AiBtn
                cfg={{
                  metric: "campaign_ctwa_origins",
                  metricName: "Anúncios",
                  metricDefinition: "Conversas agrupadas por origem de anúncio.",
                  metricValue: (ctwa?.campaigns || []).slice(0, 12),
                }}
              />
            }
          >
            {(ctwa?.campaigns || []).slice(0, 12).map((c) => {
              const open = expandedCtwaKey === c.key;
              const leads = Array.isArray(c.leads) ? c.leads : [];
              const isGoogle =
                c.trackingKind === "google_starter" ||
                String(c.provider || "").toLowerCase() === "google_ads" ||
                String(c.sourceType || "").toLowerCase() === "starter_message" ||
                String(c.sourceId || "").startsWith("starter:");
              const cost = c.mediaCost != null ? Number(c.mediaCost) : 0;
              return (
                <Box key={c.key}>
                  <CampaignRow
                    title={c.adsCampaignName || c.headline || c.sourceId || c.key}
                    primary={`${BR(c.conversations)} conversa${Number(c.conversations) === 1 ? "" : "s"} · ${
                      isGoogle ? "Google" : "Meta"
                    }`}
                    cost={cost}
                    onClick={() => setExpandedCtwaKey(open ? null : c.key)}
                    expandIcon={
                      open ? (
                        <ExpandLessIcon fontSize="small" color="action" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" color="action" />
                      )
                    }
                  />
                  <Collapse in={open}>
                    <Box sx={{ pb: 1.5, pl: 0.25 }}>
                      {!leads.length ? (
                        <Typography variant="caption" color="text.secondary">
                          Sem amostra de conversas.
                        </Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {leads.map((lead) => (
                            <Stack
                              key={lead.id}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Typography variant="caption" color="text.secondary" noWrap>
                                #{lead.id}
                                {lead.tipo ? ` · ${lead.tipo}` : ""}
                                {lead.dataCadastro
                                  ? ` · ${new Date(lead.dataCadastro).toLocaleDateString("pt-BR")}`
                                  : ""}
                              </Typography>
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/chat/${encodeURIComponent(String(lead.id))}`);
                                }}
                              >
                                Abrir
                              </Button>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {!loading && !(ctwa?.campaigns || []).length ? (
              <EmptyHint>
                Nenhum anúncio gerou conversa neste período. No Google, configure a mensagem inicial
                em Campanhas → Origem de anúncios.
              </EmptyHint>
            ) : null}
          </Panel>
        </Grid>

        <Grid item xs={12} md={6}>
          <Panel
            title="Disparos"
            subtitle="Volume e tempos de atendimento por envio."
            action={
              <AiBtn
                cfg={{
                  metric: "campaign_active_list",
                  metricName: "Disparos",
                  metricDefinition:
                    "Disparos com conversas no período, incluindo tempos médios de 1ª resposta e atendimento.",
                  metricValue: activeCampaigns.slice(0, 12),
                }}
              />
            }
          >
            {activeCampaigns.length ? (
              activeCampaigns.slice(0, 12).map((row) => {
                const id = row.campaignId;
                const cost = Number(row.cost || row.budget || 0);
                const revenue = Number(row.attributedRevenue || 0);
                const conversations = Number(row.conversations ?? row.sent ?? 0);
                return (
                  <CampaignRow
                    key={`${row.sourceKind || "c"}-${id}`}
                    title={row.campaignName || row.name || `#${id}`}
                    primary={`${BR(conversations)} conversa${conversations === 1 ? "" : "s"}`}
                    secondary={`1ª resposta ${secToHuman(row.avgTMEsec)} · atendimento ${secToHuman(
                      row.avgTMAsec
                    )}`}
                    revenue={revenue}
                    cost={cost}
                    action={
                      row.sourceKind === "broadcast" && id ? (
                        <Button size="small" onClick={() => navigate(`/campaigns/whatsapp/${id}`)}>
                          Ver disparo
                        </Button>
                      ) : null
                    }
                  />
                );
              })
            ) : (
              <EmptyHint>Nenhum disparo gerou conversa neste período.</EmptyHint>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* ——— Distribuição ——— */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Panel
            title="Por canal"
            subtitle="Onde as conversas entraram."
            action={
              <AiBtn
                cfg={{
                  metric: "campaign_by_channel",
                  metricName: "Por canal",
                  metricDefinition: "Conversas por canal de atendimento.",
                  metricValue: channelChart,
                }}
              />
            }
          >
            {loading && !channelChart.length ? (
              <DashboardKpiSkeleton height={280} />
            ) : channelChart.length ? (
              <DashboardRankedBarChart
                data={channelChart}
                categoryKey="name"
                valueKey="conversations"
                valueName="Conversas"
                fill={series[0]}
                formatValue={BR}
                height={280}
              />
            ) : (
              <EmptyHint>Sem dados de canal.</EmptyHint>
            )}
          </Panel>
        </Grid>

        <Grid item xs={12} md={6}>
          <Panel
            title="Por região"
            subtitle="Distribuição geográfica das conversas."
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  select
                  size="small"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="uf">UF</MenuItem>
                  <MenuItem value="cidade">Cidade</MenuItem>
                  <MenuItem value="bairro">Bairro</MenuItem>
                  <MenuItem value="grupo">Grupo</MenuItem>
                  <MenuItem value="custom">Rota</MenuItem>
                </TextField>
                <AiBtn
                  cfg={{
                    metric: "campaign_by_region",
                    metricName: "Por região",
                    metricDefinition: "Conversas por região no período.",
                    metricValue: regionChart,
                  }}
                />
              </Stack>
            }
          >
            {loading && !regionChart.length ? (
              <DashboardKpiSkeleton height={280} />
            ) : regionChart.length ? (
              <DashboardRankedBarChart
                data={regionChart}
                categoryKey="name"
                valueKey="conversations"
                valueName="Conversas"
                fill={series[5] || series[0]}
                formatValue={BR}
                height={280}
              />
            ) : (
              <EmptyHint>Sem dados de região.</EmptyHint>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* ——— Operação: tempos e tipos (sempre visíveis) ——— */}
      {(data?.byCampaignOps || []).length || (data?.byCampaignTipo || []).length ? (
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          {(data?.byCampaignOps || []).length ? (
            <Grid item xs={12} md={6}>
              <Panel
                title="Tempos por campanha"
                subtitle="1ª resposta e duração média do atendimento."
                action={
                  <AiBtn
                    cfg={{
                      metric: "campaign_ops_tme_tma",
                      metricName: "Tempos por campanha",
                      metricDefinition:
                        "Tempo médio até a 1ª resposta e tempo médio de atendimento por campanha.",
                      metricValue: (data.byCampaignOps || []).slice(0, 15),
                    }}
                  />
                }
              >
                {(data.byCampaignOps || []).slice(0, 12).map((row) => (
                  <Stack
                    key={`ops-${row.sourceKind}-${row.campaignId}`}
                    direction="row"
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{
                      py: 1.15,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {row.campaignName || `#${row.campaignId}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {BR(row.conversations)} conversas
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {secToHuman(row.avgTMEsec)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        1ª resposta
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.35 }}>
                        {secToHuman(row.avgTMAsec)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        atendimento
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Panel>
            </Grid>
          ) : null}

          {(data?.byCampaignTipo || []).length ? (
            <Grid item xs={12} md={(data?.byCampaignOps || []).length ? 6 : 12}>
              <Panel
                title="Tipo e subtipo"
                subtitle="Como as conversas de cada campanha foram classificadas."
                action={
                  <AiBtn
                    cfg={{
                      metric: "campaign_tipo_volume",
                      metricName: "Tipo e subtipo",
                      metricDefinition: "Volume por tipo/subtipo agrupado por campanha.",
                      metricValue: (data.byCampaignTipo || []).slice(0, 20),
                    }}
                  />
                }
              >
                {(data.byCampaignTipo || []).slice(0, 12).map((row, idx) => (
                  <Stack
                    key={`tipo-${row.sourceKind}-${row.campaignId}-${idx}`}
                    direction="row"
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{
                      py: 1.1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {row.campaignName || `#${row.campaignId}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {row.path}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                      {BR(row.conversations)}
                    </Typography>
                  </Stack>
                ))}
              </Panel>
            </Grid>
          ) : null}
        </Grid>
      ) : null}

      {/* ——— Financeiro: apoio opcional, sem roubar a cena ——— */}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 1),
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, sm: 2.75 }, "&:last-child": { pb: { xs: 2.25, sm: 2.75 } } }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 0.25 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
                  Receita e custo
                </Typography>
                <Chip
                  label="Opcional"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    letterSpacing: "0.02em",
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5, maxWidth: 560 }}>
                Aparece quando há valor na solicitação ou orçamento no disparo / anúncio.
              </Typography>
            </Box>
            <AiBtn
              cfg={{
                metric: "campaign_money_summary",
                metricName: "Receita e custo",
                metricDefinition:
                  "Receita das vendas atribuídas e custo (orçamento) quando informados.",
                metricValue: salesSummary,
              }}
            />
          </Stack>

          {!loading && hasAnyFinance ? (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                sx={{ mt: 2, mb: 2.5 }}
              >
                <FinanceSummaryChip label="Vendas" value={BR(salesSummary.sales)} />
                <FinanceSummaryChip
                  label="Receita"
                  value={salesSummary.revenue > 0 ? BRL(salesSummary.revenue) : "Sem valor"}
                  muted={!(salesSummary.revenue > 0)}
                />
                <FinanceSummaryChip
                  label="Custo"
                  value={salesSummary.cost > 0 ? BRL(salesSummary.cost) : "Sem valor"}
                  muted={!(salesSummary.cost > 0)}
                />
              </Stack>

              {!hasMoneyFilled && salesSummary.sales > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 2, lineHeight: 1.5 }}
                >
                  Há vendas atribuídas, mas ainda sem valor na solicitação nem orçamento cadastrado.
                </Typography>
              ) : null}

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: 700, letterSpacing: "0.06em" }}
                  >
                    Por tipo
                  </Typography>
                  {salesBySource.length ? (
                    <Box>
                      <FinanceColHead />
                      {salesBySource.map((row) => (
                        <FinanceRow
                          key={row.name}
                          title={row.name}
                          sales={row.sales}
                          revenue={row.revenue}
                          cost={row.cost}
                        />
                      ))}
                    </Box>
                  ) : (
                    <EmptyHint>{salesEmptyHint()}</EmptyHint>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: 700, letterSpacing: "0.06em" }}
                  >
                    Por disparo
                  </Typography>
                  {campaignSales.length ? (
                    <Box>
                      <FinanceColHead />
                      {campaignSales.map((r) => (
                        <FinanceRow
                          key={String(r.campaignId)}
                          title={r.campaignName || `#${r.campaignId}`}
                          sales={r.sales}
                          revenue={r.revenue}
                          cost={r.cost}
                        />
                      ))}
                    </Box>
                  ) : (
                    <EmptyHint>{salesEmptyHint()}</EmptyHint>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: 700, letterSpacing: "0.06em" }}
                  >
                    Por anúncio
                  </Typography>
                  {ctwaSales.length ? (
                    <Box>
                      <FinanceColHead />
                      {ctwaSales.map((r) => (
                        <FinanceRow
                          key={String(r.sourceId)}
                          title={r.headline || r.sourceId}
                          sales={r.sales}
                          revenue={r.revenue}
                          cost={r.cost}
                        />
                      ))}
                    </Box>
                  ) : (
                    <EmptyHint>{salesEmptyHint()}</EmptyHint>
                  )}
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.75, lineHeight: 1.55 }}>
              {salesEmptyHint()} O resumo financeiro entra aqui assim que houver valor ou orçamento.
            </Typography>
          )}
        </CardContent>
      </Card>

      {!readOnly ? (
        <MetricAiInsightsDialog
          open={metricDialogOpen}
          metric={metricDialogMetric}
          metricName={metricDialogName}
          metricLabel={metricDialogLabel}
          metricHuman={metricDialogHuman}
          period={{ start, end }}
          loading={metricInsightsMutation.isLoading}
          error={metricDialogError}
          data={metricDialogData}
          scope="origem"
          basePayload={metricDialogBasePayload}
          onClose={() => setMetricDialogOpen(false)}
        />
      ) : null}
    </Box>
  );
}
